import {NegotiationModel, UpdateNegotiationRequest} from "../../../../types/negotiations";
import {Popover, Skeleton, Space, Typography} from "antd";
import {States} from "../../../../types";
import dayjs from "dayjs";
import React, {Fragment, ReactNode, useCallback} from "react";
import {useSelector} from "react-redux";
import {selectUser} from "../../../../redux/features/userSlice";
import {canModifyLob} from "../../../../utils/permission";
import EditUnderwriterButton from "./modals/EditUnderwriterButton";
import EditLobButton from "./modals/EditLobButton";
import useCanModifyUnderwriterInCharge from "../../../../hooks/permissions/useCanModifyUnderwriterInCharge";
import {QuestionCircleOutlined} from "@ant-design/icons";
import {useAIFunctionality} from "../../../../utils/useAIFunctionality";

const {Paragraph, Title, Text} = Typography;

const DetailListItemAIWarning = ({ fieldData }: { fieldData: string[] | { value: string, source: string | null }[] }) => {
    if (fieldData.length === 0) {
        return <></>;
    }

    if (typeof fieldData[0] === 'string') {
        const fieldDataText = fieldData.join(', ');

        return (
            <Text type={"warning"}>
                {`Dati trovati da AI: ${fieldDataText}`}
            </Text>
        );
    }

    const fieldDataObject = fieldData.map((field, index) => {
        const castedField = field as { value: string, source: string | null };

        return (
            <Fragment key={index}>
                { castedField.value }{' '}
                { !!castedField.source && (
                    <Popover
                        placement="bottom"
                        content={`Fonte: ${castedField.source}`}
                    >
                        <QuestionCircleOutlined/>
                    </Popover>
                )}
                { index < fieldData.length - 1 && ', ' }
            </Fragment>
        );
    });

    return (
        <Text type={"warning"}>
            Dati trovati da AI:{' '}
            { fieldDataObject }
        </Text>
    );
};

const DetailListItem = ({
                            label,
                            value,
                            fieldNotAvailableMessage = 'Non presente',
                            action,
                            fieldFromRore,
                            fieldData
                        }: {
    label: string,
    value?: string | number | null | boolean,
    fieldNotAvailableMessage?: string,
    action?: ReactNode,
    fieldFromRore?: boolean,
    fieldData?: string[] | { value: string, source: string | null }[]
}) => {

    return (
        <Space direction={"vertical"} size={0}>
            <Space
                direction={'horizontal'}
                size={'middle'}
                align={'start'}>
                <span style={{wordWrap: 'break-word'}}>
                    {label}
                </span>
                <Title
                    underline={!value}
                    level={5}>
                    {value || fieldNotAvailableMessage}
                </Title>
                {action && (
                    action
                )}
            </Space>
            {fieldFromRore && !!fieldData && (
                <DetailListItemAIWarning fieldData={fieldData} />
            )}
        </Space>
    );
}

const NegotiationDetailList = ({
                                   negotiation,
                                   isLoading,
                                   editUnderwriter,
                                   updateNegotiation,
                                   isRoreSuggestionsVisible,
                               }: {
    negotiation: NegotiationModel | undefined,
    isLoading?: boolean,
    fieldNotAvailableMessage?: string,
    editUnderwriter?: (uwId: string) => Promise<NegotiationModel | undefined>,
    updateNegotiation?: (data: Partial<UpdateNegotiationRequest>) => Promise<NegotiationModel | undefined>,
    isRoreSuggestionsVisible?: boolean,
}) => {

    const user = useSelector(selectUser);
    const canModifyUnderwriterInCharge = useCanModifyUnderwriterInCharge(negotiation)

    const canShowAIFeature = useAIFunctionality();

    const fastQuotationLabel = () => {
        if (typeof negotiation?.negotiation_analysis?.is_fastquotation_suggested === "boolean") {
            if (typeof negotiation.negotiation_analysis.uw_accepted_fastquotation_suggestion === "boolean") {
                return negotiation.negotiation_analysis.uw_accepted_fastquotation_suggestion ? "SI" : "NO"
            } else {
                return negotiation?.negotiation_analysis?.is_fastquotation_suggested ? "SI Suggerito" : "NO Suggerito"
            }
        }
        return null
    }

    return (
        <>
            {!negotiation && isLoading && (
                <Skeleton active={true}/>
            )}
            {negotiation && (
                <Space direction={"vertical"} size={4}>
                    {negotiation.is_imported &&
                        <Paragraph>
                            Trattativa importata da Excel
                        </Paragraph>
                    }
                    <DetailListItem
                        label={'Contraente'}
                        value={negotiation?.contractor?.name}
                        fieldFromRore={isRoreSuggestionsVisible && !!negotiation.raw_data?.contractor?.length}
                        fieldData={negotiation?.raw_data?.contractor}
                    />
                    <DetailListItem
                        label={'LOB'}
                        value={negotiation?.lob.name}
                        action={
                            <>
                                {
                                    user &&
                                    updateNegotiation &&
                                    canModifyLob(user?.usertypes?.map(el => el.code)) &&
                                    negotiation.state === States.Rore && (
                                        <EditLobButton
                                            negotiation={negotiation}
                                            updateNegotiation={updateNegotiation}/>
                                    )
                                }
                            </>
                        }
                        fieldFromRore={isRoreSuggestionsVisible && !!negotiation.raw_data?.lob?.length}
                        fieldData={negotiation?.raw_data?.lob}
                    />
                    <DetailListItem
                        label={"Underwriter"}
                        value={negotiation?.underwriter?.user_full_name}
                        action={
                            <>
                                {
                                    canModifyUnderwriterInCharge &&
                                    editUnderwriter && (
                                        <EditUnderwriterButton
                                            negotiation={negotiation}
                                            updateUnderwriter={editUnderwriter}/>
                                    )}
                            </>
                        }
                        fieldFromRore={isRoreSuggestionsVisible && !!negotiation.raw_data?.underwriter?.length}
                        fieldData={negotiation?.raw_data?.underwriter}
                    />

                    <DetailListItem
                        label={'Data di ricezione'}
                        value={negotiation.reception_date ? dayjs(negotiation.reception_date).format('DD/MM/YYYY') : null}
                        fieldFromRore={isRoreSuggestionsVisible && !!negotiation.raw_data?.reception_date?.length}
                        fieldData={negotiation?.raw_data?.reception_date}
                    />
                    <DetailListItem
                        label={'Data di decorrenza'}
                        value={negotiation.policy_effective_date ? dayjs(negotiation.policy_effective_date).format('DD/MM/YYYY') : null}
                        fieldFromRore={isRoreSuggestionsVisible && !!negotiation.raw_data?.policy_effective_date?.length}
                        fieldData={negotiation?.raw_data?.policy_effective_date}
                    />
                    <DetailListItem
                        label={'Data di scadenza'}
                        value={negotiation.policy_expiring_date ? dayjs(negotiation.policy_expiring_date).format('DD/MM/YYYY') : null}
                        fieldFromRore={isRoreSuggestionsVisible && !!negotiation.raw_data?.policy_expiring_date?.length}
                        fieldData={negotiation?.raw_data?.policy_expiring_date}
                    />
                    <DetailListItem
                        label={'UW Year'}
                        value={negotiation?.uw_year}
                        fieldFromRore={isRoreSuggestionsVisible && !!negotiation.raw_data?.uw_year?.length}
                        fieldData={negotiation?.raw_data?.uw_year}
                    />
                    <DetailListItem
                        label={'Broker'}
                        value={negotiation?.broker?.name}
                        fieldFromRore={isRoreSuggestionsVisible && !!negotiation.raw_data?.broker?.length}
                        fieldData={negotiation?.raw_data?.broker}
                    />
                    <DetailListItem
                        label={'ISIA'}
                        value={!!negotiation?.is_isia ? 'SI' : 'NO'}
                        fieldFromRore={isRoreSuggestionsVisible && !!negotiation.raw_data?.is_isia?.length}
                        fieldData={negotiation?.raw_data?.is_isia}
                    />
                    {!!negotiation?.is_isia && (
                        <DetailListItem
                            label={'Sale ISIA'}
                            value={negotiation?.isia_sale?.user_full_name}
                            fieldFromRore={isRoreSuggestionsVisible && !!negotiation.raw_data?.isia_sale?.length}
                            fieldData={negotiation?.raw_data?.isia_sale}
                        />
                    )}
                    {!negotiation.is_isia && !!negotiation?.isia_sale &&
                        <DetailListItem
                            label={'Sale'}
                            value={negotiation?.isia_sale?.user_full_name}/>
                    }
                    {
                        canShowAIFeature
                        && <DetailListItem
                            label={'Fast Quotation'}
                            value={fastQuotationLabel()}
                            fieldNotAvailableMessage={'Ancora da analizzare'}
                        />
                    }
                </Space>
            )}
        </>
    );
}

export default NegotiationDetailList;